/**
 * 36協定の上限を「判定できているか」。**8つの状態を、利用者の言葉で。**
 *
 * <h3>なぜ「問題なし」と同じ見た目にしないのか</h3>
 *
 * 判定できなかったことと、判定した結果ぶつからなかったことは別。協定が
 * 1本も無い会社、去年で切れたまま結び直していない会社、集計値が読めない
 * 会社を「上限に触れていない」と同じ顔で出すと、<b>見ていないことが
 * 見えなくなる。</b>しかもこの製品では上限に触れる人がいない月のほうが
 * 普通なので、間違いはいちばん気づきにくい形で残る。
 *
 * だから8つとも、見出しも・短い札も・色も別にする。
 *
 * <h3>なぜ画面側に文章を持つのか</h3>
 *
 * サーバも同じ状態を説明する文を持っている。ただしそこにあるのは
 * 「なぜ判定できないか」だけで、<b>「何をすれば直るか」が無い。</b>
 * 状態だけ出しても直せないので、直し方をここで足す。覚えのない状態が
 * 来たときだけ、サーバが返した文をそのまま出す（画面が知らない状態を
 * 画面の言葉で言い換えると、嘘になる）。
 *
 * <h3>書かないもの</h3>
 *
 * 列名・API名・社内向けの言い回しは1つも書かない。読むのは買った会社の
 * 人で、この製品の中の名前を知らない。
 */

/**
 * サーバが返す状態。**この8つで全部。**
 *
 * 前の6つは<b>協定の側</b>（協定が無い・切れている・重なっている）で、
 * あとの2つは<b>実績の側</b>（集計値が読めない・在籍前）。サーバでは
 * 別々の値として返るが、画面ではどちらも「判定できていない理由」として
 * 同じ言葉で出すので、1か所にまとめて持つ。
 *
 * **2つは同時に起きる。**協定が切れていて、かつその月の集計値も読めない、
 * という状態はふつうにある。どちらか一方に丸めない。
 */
export const OVERTIME_AGREEMENT_STATUSES = [
  'valid',
  'no_agreement',
  'expired',
  'unconfirmed_system_default',
  'unsupported_non_month_start',
  'overlapping_agreements',
  'insufficient_worktime_data',
  'not_applicable',
  // 製品の側。**会社が何を入れても直らない。**
  'unsupported_variable_worktime_system',
  'unsupported_worktime_system'
]

/**
 * 8つの状態の見せ方。**1つずつ別の顔にする。**
 *
 * - `tone` … 帯の色。判定できている／会社が直す／製品が直す／読めない、で分ける
 * - `badge` … 一覧の狭い枠にも置ける短い札
 * - `headline` … 何が起きているか
 * - `detail` … なぜそうなるか
 * - `action` … 何をすれば直るか。<b>ここが無いと、読んでも直せない。</b>
 */
const NOTICES = {
  valid: {
    tone: 'ok',
    badge: '判定できています',
    headline: '上限の判定ができています',
    detail: '登録されている36協定に基づき、時間外労働の上限を確認しています。',
    action: '新しい協定を締結した場合は、自社設定の36協定の欄で「新しい協定を作る」から追加してください。'
  },
  no_agreement: {
    tone: 'danger',
    badge: '協定がありません',
    headline: '判定できません：協定の記録が登録されていません',
    detail: 'この期間に当てはまる36協定が登録されていません。'
      + '36協定が無いと、そもそも法定労働時間を超えて働いてもらうことが'
      + 'できないため、判定に使う上限がありません。',
    action: '締結している36協定の内容を、有効期間とあわせて登録してください。'
  },
  expired: {
    tone: 'danger',
    badge: '期間が終わっています',
    headline: '判定できません：協定の有効期間が終了しています',
    detail: '登録されている36協定の有効期間が、この期間より前に終わって'
      + 'います。期間が切れたことは何も起こさずに過ぎるので、'
      + '画面は去年と同じまま見え続けます。',
    action: '新しく締結した36協定を、有効期間を指定して追加してください。'
  },
  unconfirmed_system_default: {
    tone: 'warning',
    badge: '内容が未登録です',
    headline: '判定できません：協定の内容がまだ登録されていません',
    detail: '登録されているのは、この画面を空にしないために置いてある'
      + '仮の内容です。御社が締結した36協定を見て入れた値ではないため、'
      + '協定があるものとして扱いません。',
    action: '自社設定の36協定の欄で「新しい協定を作る」を選び、'
      + '協定書に書かれている適用開始日・有効期間・限度時間を'
      + '入力して登録してください。'
  },
  unsupported_non_month_start: {
    tone: 'blocked',
    badge: '対応していない形です',
    headline: '判定できません：協定の適用が月の途中から開始されています',
    detail: '上限の判定は暦月ごとに行うため、月の途中から始まる（または'
      + '月の途中で終わる）協定は、その月をどちらの協定で見るのかが'
      + '決まりません。御社の協定が誤っているという意味ではなく、'
      + 'いまのしくみで計算できない形です。',
    action: '協定の期間を月の初日から月の末日までで登録できる場合は、'
      + 'そのように登録し直してください。実際の協定が月の途中から開始される'
      + '場合は、お手数ですがサポートまでご連絡ください。'
  },
  overlapping_agreements: {
    tone: 'blocked',
    badge: '期間が重なっています',
    headline: '判定できません：同じ期間の協定が複数あります',
    detail: '同じ月に当てはまる36協定が2本以上登録されています。'
      + 'どちらで判定するのかが決まらないため、月によって答えが'
      + '変わってしまいます。',
    action: '期間が重なっている協定のどちらかを、重ならない期間に'
      + '修正してください。'
  },
  /**
   * 実績の側。**協定は有効でも、その月の集計値が読めない。**
   *
   * 勤務表をまだ保存していない月、区分の入っていない日を含む月、
   * 月の記録そのものが無い月。0時間として足すと合計が小さく出て、
   * 超過を見逃す。
   */
  insufficient_worktime_data: {
    tone: 'unknown',
    badge: '集計値が読めません',
    headline: '判定できません：この期間の集計値が読み取れません',
    detail: '協定は有効ですが、上限と突き合わせる勤務時間の集計値が'
      + '読み取れませんでした。読めない月を0時間として足すと合計が'
      + '小さく出て、超過を見逃します。',
    action: '対象月の勤務表が確定しているかご確認ください。'
      + '変わらない場合は、サポートまでご連絡ください。'
  },
  /**
   * 実績の側。**欠損ではない。**
   *
   * 入社前の月に実績が無いのは正しい状態。ここを「読み取れません」に
   * すると、4月入社の方が前の年度の12か月ぶん並び、**本当に読めていない方が
   * その中に紛れる。**
   */
  not_applicable: {
    // **'ok'（判定できています）と同じ色にしない。**判定できているのは
    // valid だけ。ここは「判定していない」が、それでよい状態。
    tone: 'outside',
    badge: '対象期間外です',
    headline: 'この月は判定の対象ではありません',
    detail: '入社前の月のため、上限を判定する実績データがありません。'
      + 'これはエラーではありません。',
    action: '入社日が実際と違う場合は、利用者の登録内容をご確認ください。'
  },
  /**
   * 製品の側。**変形労働時間制を計算できない。**
   *
   * 「勤務表を入れれば直る」と読める文言にしない——会社が何を入れても
   * 直らない。同じ言い方をすると、会社は勤務表を入れ直し続けることになる。
   *
   * **「多めに計算しています」とも書かない。**変形制には対象期間全体の
   * 法定労働時間総枠があり、日でも週でも超えていないのに総枠を超えることが
   * ある。総枠を持たないので、その時間はどこにも数えられない——
   * 見逃す側にもずれる。
   */
  unsupported_variable_worktime_system: {
    tone: 'unsupported',
    badge: '製品が未対応です',
    headline: '判定できません：変形労働時間制に対応していません',
    detail: '変形労働時間制では、あらかじめ特定された日・週について'
      + '1日8時間・週40時間を超えても直ちに法定時間外労働にはなりません。'
      + 'いまは日ごとの所定労働時間と対象期間の総枠を登録できないため、'
      + '法定時間外労働そのものを算定できません。',
    action: '対応をお待ちください。この画面の数値は表示していません。'
      + '通常の労働時間制へ変更された場合は、36協定の登録内容を'
      + 'ご確認ください。'
  },
  /** 実績の側の同じ話。文言は上とそろえる。 */
  unsupported_worktime_system: {
    tone: 'unsupported',
    badge: '時間を算定できません',
    headline: '判定できません：この勤務制度の労働時間を算定できません',
    detail: 'この勤務制度では、法定時間外労働を算定できません。'
      + '勤務表は正しく読み込めています。',
    action: '勤務表の入力は、いままでどおり続けてください。'
      + '対応後に、さかのぼって判定できます。'
  }
}

/**
 * 覚えのない状態のときに出すもの。
 *
 * <b>「問題なし」へ寄せない。</b>知らない値が来たとき、それは上限に
 * 触れていないことの証拠ではない。空文字も、綴りの違う文字列もここへ来る。
 */
const UNKNOWN_NOTICE = {
  tone: 'unknown',
  badge: '確認できません',
  headline: '上限を判定できているか確認できません',
  detail: '判定の状態を取得できませんでした。',
  action: '画面を再読み込みしても解決しない場合は、サポートまでご連絡ください。'
}

/**
 * 状態を、画面に出す形へ。
 *
 * @param {string} status サーバが返した状態
 * @param {string} [serverMessage] サーバが返した説明。
 *        <b>覚えのない状態のときだけ使う。</b>知っている状態を
 *        サーバの文で上書きすると、直し方（action）と噛み合わなくなる。
 * @returns {{key: string, tone: string, badge: string, headline: string,
 *            detail: string, action: string, judged: boolean}}
 */
export function overtimeAgreementStatusNotice (status, serverMessage) {
  const key = String(status == null ? '' : status).trim()
  const known = Object.prototype.hasOwnProperty.call(NOTICES, key)
    ? NOTICES[key]
    : null

  if (known) {
    return {
      key: key,
      tone: known.tone,
      badge: known.badge,
      headline: known.headline,
      detail: known.detail,
      action: known.action,
      judged: key === 'valid'
    }
  }

  const detail = String(serverMessage == null ? '' : serverMessage).trim()
  return {
    key: 'unknown',
    tone: UNKNOWN_NOTICE.tone,
    badge: UNKNOWN_NOTICE.badge,
    headline: UNKNOWN_NOTICE.headline,
    detail: detail || UNKNOWN_NOTICE.detail,
    action: UNKNOWN_NOTICE.action,
    judged: false
  }
}

/**
 * 帯に付けるクラス。**状態ごとに別のクラスを1つずつ付ける。**
 *
 * 色（tone）だけだと、判定できない7つのうち何組かが同じ見た目になる。
 * 状態そのものの名前も付けて、どれとどれも重ならないようにする。
 *
 * @param {{key: string, tone: string}} notice
 * @param {string} [prefix] クラス名の頭。画面ごとに変える
 * @returns {string[]}
 */
export function overtimeAgreementStatusClasses (notice, prefix) {
  const head = String(prefix || 'agreement-status')
  const source = notice || {}
  return [
    head + '--' + String(source.tone || 'unknown'),
    head + '--is-' + String(source.key || 'unknown')
  ]
}
